import React, { useState } from 'react';
import { CreditCard, ArrowUpRight, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { openBillingPortal, startCheckout } from '../lib/billing';
import { trackManageSubscriptionClicked } from '../lib/analytics';
import { Skeleton } from './Skeleton';

/**
 * Component to display subscription information in Profile
 * @param {Object} props
 * @param {Object} props.subscription - Subscription data from useSubscription
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onRefresh - Refresh handler
 */
export default function ProfileSubscriptionCard({ subscription, loading, onRefresh }) {
  const [managingSubscription, setManagingSubscription] = useState(false);

  if (loading) {
    return (
      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="h-5 w-32 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
        <Skeleton className="h-10 w-40" />
      </section>
    );
  }

  if (!subscription) {
    return (
      <section className="card">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-amber-600" size={20} />
          <div>
            <p className="font-semibold text-amber-800 dark:text-amber-200">
              Abonnementsinformatie niet beschikbaar
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Probeer de pagina te vernieuwen of neem contact op met ondersteuning als dit blijft gebeuren.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const {
    plan = 'free',
    status = 'active',
    is_pro: isPro = false,
    period_end: periodEnd,
    cancel_at_period_end: cancelAtPeriodEnd = false,
    topic_quotas: topicQuotas = {}
  } = subscription;

  const planName = getPlanDisplayName(plan);
  const statusBadge = getStatusBadge(status, cancelAtPeriodEnd);

  // Calculate total usage across all topics
  const totalUsed = Object.values(topicQuotas).reduce((sum, quota) => sum + (quota.used || 0), 0);
  const hasUsage = totalUsed > 0;

  // Get topics by usage
  const topTopics = Object.entries(topicQuotas)
    .map(([id, quota]) => ({ id, ...quota }))
    .sort((a, b) => (b.used || 0) - (a.used || 0))
    .filter(t => t.used > 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'n.v.t.';
    try {
      return new Date(dateStr).toLocaleDateString('nl-NL', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'n.v.t.';
    }
  };

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true);
      const returnUrl = `${window.location.origin}/profile`;

      if (isPro) {
        trackManageSubscriptionClicked();
        await openBillingPortal(returnUrl);
        return;
      }

      await startCheckout(null, returnUrl);
    } catch (error) {
      console.error('Failed to start billing flow:', error);
      toast.error(error.message || 'Het starten van de abonnementsflow is mislukt. Probeer het opnieuw of neem contact op met ondersteuning.');
      setManagingSubscription(false);
    }
  };

  return (
    <section className="card space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-secondary-800 dark:text-primary-300">
          <CreditCard size={18} />
        </span>
        <div className="flex-1">
          <h2 className="m-0 text-base font-semibold">Abonnement</h2>
          <p className="text-sm text-secondary-600 dark:text-secondary-400">
            Beheer je abonnement en facturatie
          </p>
        </div>
      </div>

      {/* Plan info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-secondary-50 dark:bg-secondary-800/50 border border-secondary-200 dark:border-secondary-700">
          <div className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">
            Huidig abonnement
          </div>
          <div className="flex items-center gap-2">
            <div className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
              {planName}
            </div>
            {statusBadge}
          </div>
          {!isPro && (
            <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
              Beperkt aantal vragen per onderwerp
            </div>
          )}
        </div>

        <div className="p-4 rounded-lg bg-secondary-50 dark:bg-secondary-800/50 border border-secondary-200 dark:border-secondary-700">
          <div className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1">
            {isPro ? 'Volgende factuurdatum' : 'Vragenlimiet'}
          </div>
          <div className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">
            {isPro ? formatDate(periodEnd) : 'Levenslang'}
          </div>
          {!isPro && (
            <div className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
              Maximaal 5 unieke vragen per onderwerp voor de levensduur van je account.
            </div>
          )}
          {cancelAtPeriodEnd && (
            <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
              Abonnement wordt op deze datum opgezegd
            </div>
          )}
        </div>
      </div>

      {/* Usage info */}
      {hasUsage && !isPro && (
        <div className="p-4 rounded-lg bg-primary-50/50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800">
          <div className="text-xs font-medium text-primary-800 dark:text-primary-200 mb-2">
            Gebruik per onderwerp
          </div>
          <div className="space-y-2">
            {topTopics.map((topic) => (
              <div key={topic.id} className="flex items-center justify-between text-sm">
                <span className="text-primary-900 dark:text-primary-100">{topic.topic_name}</span>
                <span className="text-primary-700 dark:text-primary-300 font-medium">
                  {topic.used} / {topic.limit === -1 ? '∞' : topic.limit}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          className="btn-secondary flex items-center justify-center gap-2"
          onClick={handleManageSubscription}
          disabled={managingSubscription}
        >
          {managingSubscription ? 'Bezig met openen...' : (isPro ? 'Abonnement beheren' : 'Upgrade naar Pro')}
          <ArrowUpRight size={16} />
        </button>
        {onRefresh && (
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onRefresh}
          >
            Vernieuwen
          </button>
        )}
      </div>

      <p className="text-xs text-secondary-500 dark:text-secondary-500">
        {isPro
          ? 'Beheer je abonnement, werk betaalmethoden bij of zeg op via het Stripe-facturatieportaal.'
          : 'Start je Pro-abonnement via Stripe Checkout.'}
      </p>
    </section>
  );
}

function getPlanDisplayName(planId) {
  const names = {
    free: 'Gratis',
    pro_monthly: 'Pro (maandelijks)',
    pro_yearly: 'Pro (jaarlijks)'
  };
  return names[planId] || planId;
}

function getStatusBadge(status, cancelAtPeriodEnd) {
  if (cancelAtPeriodEnd) {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
        Wordt opgezegd
      </span>
    );
  }

  const badges = {
    active: (
      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 font-medium">
        Actief
      </span>
    ),
    past_due: (
      <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 font-medium">
        Betaling achterstallig
      </span>
    ),
    canceled: (
      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300 font-medium">
        Geannuleerd
      </span>
    ),
    incomplete: (
      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
        Onvolledig
      </span>
    )
  };

  return badges[status] || null;
}
